import { atom, selector } from 'recoil'
import { curry, concat, length, pluck, filter, pipe, uniq, groupBy, prop, mapObjIndexed, values, map, flatten, groupWith, equals, sort, identity, uniqBy, fromPairs, reverse, uniqWith, toPairs, keys, reduce, append, find, findIndex } from 'ramda'
import { getDbId, getStorage, isImage, labelForUser, what } from '../lib/utils'

export const icRawDataState = atom({
  key: 'icRawDataState',
  default: []
})


export const icLinksDataState = atom({
  key: 'icLinksDataState',
  default: {}
})

export const icDataState = selector({
  key: 'icDataState',
  get: ({ get }) => {
    const icLinkData = get(icLinksDataState)
    const linkedData = pipe(
      values,
      filter(thing => Array.isArray(thing)),
      flatten
    )(icLinkData)
    return pipe(
      concat(linkedData),
      reverse, // reverse so uniq will take the latest
      uniqWith((a, b) => a.dId === b.dId && a.from === b.from && a.to === b.to),
      reverse // put it back so newest are at the end
    )(get(icRawDataState))
  }
})

export const icLatestDataState = selector({
  key: 'icLatestDataState',
  get: ({ get }) => {
    const data = get(icDataState)
    const lastIcSave = get(lastIcSaveState)
    if (lastIcSave) {
      return filter(t => t.time > lastIcSave, data)
    }
    return []
  }
})

export const icConnectedState = atom({
  key: 'icConnectedState',
  default: false
})

export const ipfsState = atom({
  key: 'ipfsState',
  default: {}
})

export const icStorageState = atom({
  key: 'icStorageState',
  default: false
})

export const icAddTagState = atom({
  key: 'icAddTagState',
  default: []
})

export const icExportDataState = atom({
  key: 'icExportDataState',
  default: false
})

export const icTopics = selector({
  key: 'icTopics',
  get: ({ get }) => {
    const data = get(icDataState)
    const icAddress = get(icConnectedState)
    const icSettings = get(icSettingsState)
    const dbId = getDbId(icAddress)
    let linkedDbs = []
    if (icSettings.links) {
      linkedDbs = pipe(
        keys,
        map(getDbId)
      )(icSettings.links)
    }
    return pipe(
      filter(t => {
        return t.to !== dbId && !linkedDbs.includes(t.to)
      }),
      groupBy(prop('to')),
      mapObjIndexed((val, to) => {
        return {
          to,
          count: length(val)
        }
      }),
      values
    )(data)
  }
})

export const icTagsState = selector({
  key: 'icTags',
  get: ({ get }) => {
    const data = get(icDataState)
    return pipe(
      map(t => [t.to, t.from]),
      flatten,
      groupBy(identity),
      mapObjIndexed((value, key) => ({ tag: key, count: value.length })),
      values,
      sort((a, b) => b.length - a.length)
    )(data)
  }
})

export const icImagesState = selector({
  key: 'icImages',
  get: ({ get }) => {
    const data = get(icDataState)
    const obviousImages = flatten(data
      .filter(t => isImage(t.from) || isImage(t.to))
      .map(t => {
        if (isImage(t.from) && isImage(t.to)) {
          return [t.from, t.to]
        }
        if (isImage(t.from)) {
          return t.from
        }
        return t.to
      }))
    const imageMimeTos = data.filter(t => t.from.startsWith('image/')).map(t => t.to)
    const ipfsImages = flatten(data
      .filter(t => t.from.startsWith('/ipfs/') || t.to.startsWith('/ipfs/'))
      .filter(t => imageMimeTos.includes(t.from) || imageMimeTos.includes(t.to))
      .map(t => {
        if (imageMimeTos.includes(t.from) && imageMimeTos.includes(t.to)) {
          return [t.from, t.to]
        }
        if (imageMimeTos.includes(t.from)) {
          return t.from
        }
        return t.to
      }))
    return uniq(concat(obviousImages, ipfsImages))
  }
})

const addAddressAndId = curry((addr, obj) => {
  return {
    ...obj,
    address: addr,
    id: getDbId(addr)
  }
})
export const icSettingsState = selector({
  key: 'icSettingsState',
  get: ({ get }) => {
    const data = get(icDataState)
    const icLinksData = get(icLinksDataState)
    const icAddress = get(icConnectedState)
    const regex = /^\|([^ |]+)\|/
    const settings = pipe(
      filter(tag => regex.test(tag.from) && tag.yesNo === '+'),
      uniqBy(prop('from')),
      map(tag => {
        let key = tag.from.match(regex)[1]
        let val = tag.from.replace(regex, '')
        const isArray = /\[\]$/.test(key)
        if (isArray) {
          key = key.replace(/\[\]$/, '')
          val = [val]
        }
        return [key, val]
      }),
      reduce((acc, arr) => {
        let ret = arr
        if (Array.isArray(arr[1])) {
          const exisitingArrI = findIndex(a => arr[0] === a[0], acc)
          if (exisitingArrI > -1) {
            acc[exisitingArrI] = [arr[0], append(arr[1][0], acc[exisitingArrI][1])]
            return acc
          }
        }
        return append(ret, acc)
      }, []),
      fromPairs,
      addAddressAndId(icAddress)
    )(data)
    const links = toPairs(icLinksData)
    if (links.length > 0) {
      settings.links = {}
      links.forEach(arr => {
        settings.links[arr[0]] = addAddressAndId(arr[0], {
          data: arr[1] === 'loading' ? 'loading' : arr[1].length
        })
      })
    }
    return settings
  }
})

export const icIsesState = selector({
  key: 'icIsesState',
  get: ({ get }) => {
    const data = get(icDataState)
    const regex = /^:is:/
    const ises = pipe(
      filter(tag => regex.test(tag.from)),
      groupBy(tag => tag.to + tag.from),
      mapObjIndexed((uniqTags, tag) => {
        return {
          count: uniqTags.length,
          sum: uniqTags.reduce((acc, val) => val.yesNo === '-' ? acc - 1 : acc + 1, 0),
          yes: uniqTags.filter(t => t.yesNo !== '-').length,
          from: uniqTags[0].from,
          to: uniqTags[0].to
        }
      }),
      values,
      filter(tag => tag.sum > 0)
    )(data)
    return ises
  }
})

export const isMineFsState = selector({
  key: 'isMineFsState',
  get: ({ get }) => {
    const icSettings = get(icSettingsState)
    const icFileServerUrl = get(icFileServerUrlState)
    const icFileServerUser = get(icFileServerUserState)
    const regex = new RegExp(`${icFileServerUrl}/${icFileServerUser}/`)
    return regex.test(icSettings.address) || icSettings.address === icFileServerUser
  }
})

export const sortIState = atom({
  key: 'sortIState',
  default: 0
})

export const filterIState = atom({
  key: 'filterIState',
  default: 1
})

export const lensState = atom({
  key: 'lensState',
  default: false
})

export const breadcrumbState = atom({
  key: 'breadcrumbState',
  default: []
})

export const pinataJwtState = atom({
  key: 'pinataJwtState',
  default: getStorage('pinataJwt') || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1c2VySW5mb3JtYXRpb24iOnsiaWQiOiJhYzkzNDBlMy1jMzllLTRlZTMtYjc4My1kODQxNGFmNWE2YWYiLCJlbWFpbCI6Im93aXNlMUBnbWFpbC5jb20iLCJlbWFpbF92ZXJpZmllZCI6dHJ1ZSwicGluX3BvbGljeSI6eyJyZWdpb25zIjpbeyJpZCI6Ik5ZQzEiLCJkZXNpcmVkUmVwbGljYXRpb25Db3VudCI6MX1dLCJ2ZXJzaW9uIjoxfSwibWZhX2VuYWJsZWQiOmZhbHNlLCJzdGF0dXMiOiJBQ1RJVkUifSwiYXV0aGVudGljYXRpb25UeXBlIjoic2NvcGVkS2V5Iiwic2NvcGVkS2V5S2V5IjoiYTcwMWMwNGE4YjE0NTg0MjE2YWYiLCJzY29wZWRLZXlTZWNyZXQiOiI5ZjA0N2ZkNjhlNjhjODg0MjZhMWI5ZTM2YjM5NmM5YjkwN2Q2N2E0NjE1NjVmYTMzMTkyMjcyNzRlNWFlNTJkIiwiaWF0IjoxNjc0ODM2MDc4fQ.8g81soKkCuGCYYabVYXjCSrblhGjf5bI0B3z7vBfuHw'
})

export const icFileServerUrlState = atom({
  key: 'icFileServerUrlState',
  default: getStorage('icFileServerUrl') || 'https://ic-file-server-tmm4c.ondigitalocean.app'
})

export const icFileServerUserState = atom({
  key: 'icFileServerUserState',
  default: getStorage('icFileServerUser') || ''
})

export const icFileServerState = atom({
  key: 'icFileServerState',
  default: {
    url: getStorage('icFileServerUrl') || '',
    user: getStorage('icFileServerUser') || ''
  }
})

export const perspectivesState = atom({
  key: 'perspectivesState',
  default: getStorage('perspectivesState') || []
})

export const thingsHappeningState = atom({
  key: 'thingsHappeningState',
  default: []
})

export const icfsConfigState = atom({
  key: 'icfsConfigState',
  default: {}
})

export const lastIcSaveState = atom({
  key: 'lastIcSaveState',
  default: null
})

export const selectedTagsState = atom({
  key: 'selectedTagsState',
  default: []
})

export const userLabelState = selector({
  key: 'userLabelState',
  get: ({ get }) => {
    const icFileServerUser = get(icFileServerUserState)
    const perspectives = get(perspectivesState)
    return labelForUser(icFileServerUser, perspectives)
  }
})

export const knownIcServersState = atom({
  key: 'knownIcServersState',
  default: null
})
